"use client"

import type { ReactNode } from "react"
import { createContext, useContext, useMemo } from "react"
import { toast } from "sonner"
import { TToastLocationDismissal } from "./TToastLocationDismissal"

export type TToastKey = string | number

interface TToast {
	show(message: ReactNode, options?: object): TToastKey
	success(message: ReactNode, options?: object): TToastKey
	warning(message: ReactNode, options?: object): TToastKey
	error(message: ReactNode, options?: object): TToastKey
	info(message: ReactNode, options?: object): TToastKey
	close(key: TToastKey): void
}

const TToastContext = createContext<TToast | null>(null)

export const useToast = (): TToast => {
	const context = useContext(TToastContext)
	if (!context) {
		throw new Error("useToast must be used within a TToastProvider")
	}
	return context
}

export const TToastProvider = ({ children, closeOnLocationChange = true }: { children: ReactNode; closeOnLocationChange?: boolean }) => {
	const instance = useMemo(
		() => ({
			show(message: ReactNode, options?: object): TToastKey {
				const id = toast(message, {
					...options
				})
				return id
			},
			success(message: ReactNode, options?: object): TToastKey {
				const id = toast.success(message, {
					icon: (
						<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2ZM12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4ZM14.3174 9.26855C14.7211 8.89175 15.3546 8.91364 15.7314 9.31738C16.1082 9.72114 16.0864 10.3546 15.6826 10.7314L11.3965 14.7314C11.0124 15.0897 10.4164 15.0896 10.0322 14.7314L8.31738 13.1309C7.91396 12.7541 7.89204 12.1214 8.26855 11.7178C8.64539 11.314 9.27887 11.2921 9.68262 11.6689L10.7139 12.6318L14.3174 9.26855Z"
								fill="#00C867"
							/>
						</svg>
					),
					...options
				})
				return id
			},
			warning(message: ReactNode, options?: object): TToastKey {
				const id = toast.warning(message, {
					icon: (
						<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M10.2559 4.0752C11.0191 2.70942 12.9848 2.7094 13.748 4.0752L21.5439 18.0254C22.2883 19.3583 21.3245 21.0006 19.7979 21.001H4.20605C2.67913 21.0008 1.71527 19.3584 2.45996 18.0254L10.2559 4.0752ZM4.20605 19.001H19.7979L12.002 5.05078L4.20605 19.001ZM12.002 15.001C12.5541 15.0011 13.002 15.4488 13.002 16.001C13.0019 16.5531 12.5541 17.0008 12.002 17.001C11.4497 17.001 11.002 16.5532 11.002 16.001C11.002 15.4487 11.4497 15.001 12.002 15.001ZM12.002 9.00098C12.5541 9.00114 13.002 9.44879 13.002 10.001V13.001C13.0019 13.5531 12.5541 14.0008 12.002 14.001C11.4497 14.001 11.002 13.5532 11.002 13.001V10.001C11.002 9.44869 11.4497 9.00098 12.002 9.00098Z"
								fill="#FFC300"
							/>
						</svg>
					),
					...options
				})
				return id
			},
			error(message: ReactNode, options?: object): TToastKey {
				const id = toast.error(message, {
					icon: (
						<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2ZM12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4ZM12 15C12.5523 15 13 15.4477 13 16C13 16.5523 12.5523 17 12 17C11.4477 17 11 16.5523 11 16C11 15.4477 11.4477 15 12 15ZM12 7C12.5523 7 13 7.44772 13 8V13C13 13.5523 12.5523 14 12 14C11.4477 14 11 13.5523 11 13V8C11 7.44772 11.4477 7 12 7Z"
								fill="#F33C3C"
							/>
						</svg>
					),
					...options
				})
				return id
			},
			info(message: ReactNode, options?: object): TToastKey {
				const id = toast.info(message, {
					icon: (
						<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2ZM12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4ZM12.1025 10.0049C12.6067 10.0562 13 10.4823 13 11V15C13.5523 15 14 15.4477 14 16C14 16.5523 13.5523 17 13 17H11C10.4477 17 10 16.5523 10 16C10 15.4477 10.4477 15 11 15V12C10.4477 12 10 11.5523 10 11C10 10.4477 10.4477 10 11 10H12L12.1025 10.0049ZM12 7C12.5523 7 13 7.44772 13 8C13 8.55228 12.5523 9 12 9C11.4477 9 11 8.55228 11 8C11 7.44772 11.4477 7 12 7Z"
								fill="#3A87F4"
							/>
						</svg>
					),
					...options
				})
				return id
			},
			close: (key: TToastKey): void => {
				toast.dismiss(key)
			}
		}),
		[]
	)

	return (
		<TToastContext.Provider value={instance}>
			{children}
			{closeOnLocationChange && <TToastLocationDismissal />}
		</TToastContext.Provider>
	)
}
